import React, { useEffect, useState } from 'react';

export const REVIEW_FEEDBACK_REASONS = [
  { value: 'too_far_off', label: 'Too far off to fix' },
  { value: 'not_a_meal', label: 'Not a full meal' },
  { value: 'bad_extraction', label: 'Unusable extraction / garbled recipe' },
  { value: 'wrong_category', label: 'Wrong category (side, dessert, snack…)' },
  { value: 'duplicate', label: 'Duplicate of a dish we already have' },
  { value: 'quality', label: 'Quality is not worth publishing' },
  { value: 'other', label: 'Other' }
];

export const REVIEW_REASON_LABELS = REVIEW_FEEDBACK_REASONS.reduce((map, reason) => {
  map[reason.value] = reason.label;
  return map;
}, {});

/**
 * Collect why a reviewer is hiding or flagging a menu so the note can teach
 * later agent runs. Used from Menu Management.
 */
const MenuReviewFeedbackModal = ({
  isOpen,
  mode = 'feedback',
  itemTitle = '',
  loading = false,
  onCancel,
  onSubmit,
  onHideWithoutFeedback
}) => {
  const [reason, setReason] = useState('too_far_off');
  const [note, setNote] = useState('');
  const [alsoHide, setAlsoHide] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setReason(mode === 'hide' ? 'too_far_off' : 'other');
    setNote('');
    setAlsoHide(mode === 'hide');
  }, [isOpen, mode]);

  if (!isOpen) return null;

  const isHide = mode === 'hide';
  const hasContent = Boolean(note.trim()) || Boolean(reason);

  const handleSubmit = () => {
    if (!hasContent || loading) return;
    onSubmit({
      reason,
      note: note.trim(),
      hide: isHide || alsoHide
    });
  };

  return (
    <div
      className="confirm-modal-overlay"
      role="presentation"
      onClick={loading ? undefined : onCancel}
    >
      <div
        className="confirm-modal-dialog review-feedback-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-feedback-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="review-feedback-title" className="confirm-modal-title">
          {isHide ? 'Hide this recipe?' : 'Add reviewer feedback'}
        </h2>
        <p className="confirm-modal-message">
          {isHide
            ? `Leave a note on “${itemTitle}” so later agent runs can avoid the same class of recipe. Hide used to be the only signal.`
            : `What should later agent runs learn from “${itemTitle}”?`}
        </p>

        <div className="review-feedback-reasons" role="group" aria-label="Feedback reason">
          {REVIEW_FEEDBACK_REASONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`review-feedback-chip ${reason === option.value ? 'selected' : ''}`}
              onClick={() => setReason(option.value)}
              disabled={loading}
            >
              {option.label}
            </button>
          ))}
        </div>

        <label className="review-feedback-note-label" htmlFor="review-feedback-note">
          Details (optional, but useful)
          <textarea
            id="review-feedback-note"
            className="review-feedback-note"
            rows={4}
            maxLength={2000}
            placeholder="e.g. This is a cucumber salad, not a dinner. The agent keeps pulling single-component sides."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={loading}
          />
        </label>

        {!isHide && (
          <label className="review-feedback-hide-toggle">
            <input
              type="checkbox"
              checked={alsoHide}
              onChange={(e) => setAlsoHide(e.target.checked)}
              disabled={loading}
            />
            Also hide this recipe from the public menu
          </label>
        )}

        <div className="confirm-modal-actions">
          <button
            type="button"
            className="confirm-modal-btn confirm-modal-btn-cancel"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          {isHide && onHideWithoutFeedback && (
            <button
              type="button"
              className="confirm-modal-btn confirm-modal-btn-cancel"
              onClick={onHideWithoutFeedback}
              disabled={loading}
            >
              Hide without feedback
            </button>
          )}
          <button
            type="button"
            className="confirm-modal-btn confirm-modal-btn-primary"
            onClick={handleSubmit}
            disabled={loading || !hasContent}
          >
            {loading
              ? 'Saving…'
              : (isHide || alsoHide ? 'Save feedback and hide' : 'Save feedback')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MenuReviewFeedbackModal;
